import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Rocket, Check } from 'lucide-react';
import { brand } from '@iheart/shared';
import { MotionButton, GradientText, cn } from '@iheart/ui';
import { api, ApiError } from '../lib/api';
import { getRecaptchaToken } from '../lib/recaptcha';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { PublicFooter, PublicNav } from '../components/PublicFooter';

const PLANS = [
  {
    id: 'FREE_TRIAL',
    name: 'Free Trial',
    price: 'Free',
    period: '14 days',
    desc: 'Try iHeart CNTRL with Facebook & Instagram.',
    features: ['1 workspace', '2 social accounts', '10 scheduled posts', 'Content calendar', 'Link in Bio', 'Domina Agent (non-AI) — free', 'Domina AI via token top-ups'],
  },
  {
    id: 'PROFESSIONAL',
    name: 'Professional',
    price: 'R799',
    period: '/month',
    desc: 'For growing businesses ready to level up.',
    popular: true,
    features: ['1 workspace · 1 user', '+R399/mo per extra user', '+R799/mo per extra workspace', '20 posts/month', 'Content calendar & Planner', 'Domina Agent (non-AI) — free', 'Domina AI — token top-ups'],
  },
  {
    id: 'AGENCY',
    name: 'Agency',
    price: 'R9,999',
    period: '/mo',
    desc: 'R9,999 per user per workspace — from R75,000/mo for multiple users & workspaces.',
    features: ['R9,999 per user / workspace', 'From R75,000 for multiple', 'Everything in Professional', 'PR, analytics, listening, Engage & CRM', 'Media library + AI images', 'SEO/GEO + heatmaps', 'Domina AI – 20 chats + 20 images/mo + top-ups', 'Dedicated account manager'],
  },
];

export function RegisterRoute() {
  useDocumentTitle('Register for Early Access');
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get('plan')?.toUpperCase() ?? '';
  const [plan, setPlan] = useState(preselected || '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPlan = PLANS.find((p) => p.id === plan);
  const isPaid = plan === 'PROFESSIONAL' || plan === 'AGENCY';
  const [showPlanHint, setShowPlanHint] = useState(false);

  const handleButtonClick = (e: React.MouseEvent) => {
    if (!plan) { e.preventDefault(); setShowPlanHint(true); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) return;
    setError(null);
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken('register');
      await api('/auth/early-access', {
        method: 'POST',
        body: {
          name,
          email,
          company: company || undefined,
          phone: phone || undefined,
          plan,
          message: message || undefined,
          recaptchaToken,
        },
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | null;
        setError(body?.message ?? 'Something went wrong. Please try again.');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-full flex-col items-center overflow-hidden bg-neutral-50 px-4 py-12 pt-20">
      <PublicNav />
      <div
        className="pointer-events-none absolute -left-40 -top-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 -right-40 size-[500px] rounded-full opacity-10 blur-3xl"
        style={{ backgroundImage: brand.gradient.primary }}
      />

      {/* Success */}
      {submitted ? (
        <div className="relative w-full max-w-sm rounded-2xl border border-white/60 bg-white/80 p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl">
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div
              className="flex size-14 items-center justify-center rounded-full text-white"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              <CheckCircle2 className="size-7" />
            </div>
            <h2 className="text-lg font-semibold text-neutral-800">Request Received!</h2>
            <p className="text-sm leading-relaxed text-neutral-500">
              {isPaid
                ? `Thanks for choosing the ${selectedPlan?.name} plan! Our team will be in touch shortly with an invoice and to get your account set up.`
                : 'Thanks for your interest in iHeart CNTRL! Our team will review your request and get you set up with early access shortly.'}
            </p>
            <Link
              to="/"
              className="mt-2 text-sm font-medium text-pink-600 transition-colors hover:text-pink-700"
            >
              &larr; Back to homepage
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="relative mb-8 text-center">
            <img src="/logo.png" alt="iHeart CNTRL" className="mx-auto mb-4 size-16" />
            <h1 className="text-2xl font-bold text-neutral-800">
              Register for <GradientText>Early Access</GradientText>
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-neutral-500">
              Choose your plan and register your interest. We'll get in touch to set up your account
              {isPaid ? ' and send you an invoice' : ''}.
            </p>
          </div>

          {/* Step 1: Plan selection */}
          <div className="relative mb-8 grid grid-cols-1 w-full max-w-3xl gap-4 sm:grid-cols-3">
            {PLANS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setPlan(p.id); setShowPlanHint(false); }}
                className={cn(
                  'relative rounded-2xl border-2 p-5 text-left transition-all',
                  plan === p.id
                    ? 'border-pink-500 bg-white shadow-lg shadow-pink-100'
                    : 'border-neutral-200 bg-white/80 hover:border-neutral-300 hover:shadow-sm',
                )}
              >
                {p.popular && (
                  <span className="absolute -top-2.5 right-4 rounded-full bg-gradient-to-r from-pink-500 to-indigo-500 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                    Most Popular
                  </span>
                )}
                <div className="mb-3">
                  <h3 className="text-sm font-bold text-neutral-800">{p.name}</h3>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="text-xl font-bold text-neutral-800">{p.price}</span>
                    <span className="text-xs text-neutral-400">{p.period}</span>
                  </div>
                </div>
                <p className="mb-3 text-xs text-neutral-500">{p.desc}</p>
                <ul className="flex flex-col gap-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5 text-xs text-neutral-600">
                      <Check className="mt-0.5 size-3 shrink-0 text-emerald-500" />
                      {f}
                    </li>
                  ))}
                </ul>
                {plan === p.id && (
                  <div className="absolute right-3 top-3 flex size-5 items-center justify-center rounded-full bg-pink-500 text-white">
                    <Check className="size-3" />
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Invoice note for paid plans */}
          {isPaid && (
            <div className="relative mb-6 w-full max-w-sm rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-xs text-amber-700">
              <strong>Payment by invoice</strong> — we'll send you an invoice after reviewing your registration. No upfront payment required to register.
            </div>
          )}

          {/* Step 2: Details form */}
          <div className="relative w-full max-w-sm rounded-2xl border border-white/60 bg-white/80 p-8 shadow-[0_4px_30px_rgba(0,0,0,0.06)] backdrop-blur-xl">
            <h2 className="mb-4 text-center text-base font-semibold text-neutral-800">
              {plan ? 'Your Details' : 'Select a plan above, then fill in your details'}
            </h2>
            <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="reg-name" className="mb-1 block text-sm font-medium text-neutral-700">
                  Full Name <span className="text-pink-500">*</span>
                </label>
                <input
                  id="reg-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label htmlFor="reg-email" className="mb-1 block text-sm font-medium text-neutral-700">
                  Email <span className="text-pink-500">*</span>
                </label>
                <input
                  id="reg-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label htmlFor="reg-company" className="mb-1 block text-sm font-medium text-neutral-700">
                  Company / Brand <span className="text-neutral-400">(optional)</span>
                </label>
                <input
                  id="reg-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                  placeholder="Your company or brand name"
                />
              </div>
              <div>
                <label htmlFor="reg-phone" className="mb-1 block text-sm font-medium text-neutral-700">
                  Phone / Mobile <span className="text-neutral-400">(optional)</span>
                </label>
                <input
                  id="reg-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                  placeholder="+27 XX XXX XXXX"
                />
              </div>
              <div>
                <label htmlFor="reg-message" className="mb-1 block text-sm font-medium text-neutral-700">
                  Tell us about your needs <span className="text-neutral-400">(optional)</span>
                </label>
                <textarea
                  id="reg-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                  placeholder="How many social accounts, team size, agency or brand..."
                />
              </div>

              {error && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </div>
              )}

              {!plan && showPlanHint && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 text-center">
                  Please select a plan above to continue
                </div>
              )}

              <MotionButton
                type="submit"
                size="lg"
                className={cn('mt-1 w-full gap-2', !plan && '!bg-neutral-300 !bg-none !text-neutral-500')}
                loading={submitting}
                disabled={submitting}
                onClick={handleButtonClick}
              >
                <Rocket className="size-4" />
                {submitting ? 'Submitting…' : isPaid ? 'Register & Request Invoice' : 'Request Early Access'}
              </MotionButton>

              <p className="mt-2 text-center text-xs text-neutral-400">
                Already have access?{' '}
                <Link to="/login" className="font-medium text-pink-600 transition-colors hover:text-pink-700">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </>
      )}

      <PublicFooter />
    </div>
  );
}
